import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import { aiApi } from "../../api/ai.api";
import { useAuthStore } from "../../store/auth.store";
import { studentErrorMessage } from "./student-shared";
import "./student-draft.css";

const OFFICE_ROLES = ["owner", "manager", "agent"];

/**
 * « Reformuler » (J47) : Permio AI propose une autre version du message écrit, avant l'envoi. La
 * personne garde son texte ou prend la version proposée, qu'elle peut encore corriger ; rien ne
 * part sans elle. Comptes du bureau qui écrivent, sous l'abonnement Permio AI.
 */
export function Rephrase({
  text,
  disabled = false,
  onUse,
}: {
  text: string;
  /** Envoi en cours : la saisie est figée. */
  disabled?: boolean;
  onUse: (text: string) => void;
}) {
  const { user, activeSchoolId } = useAuthStore();
  const allowed = OFFICE_ROLES.includes(user?.role ?? "");
  // Même état que la conversation de Permio : sans abonnement ni service, aucun bouton.
  const status = useQuery({
    queryKey: ["ai-status", activeSchoolId ?? "", user?.id ?? ""],
    queryFn: aiApi.status,
    enabled: allowed,
    retry: false,
    gcTime: 0,
  });
  // Texte reformulé : la proposition ne vaut que pour lui.
  const [source, setSource] = useState("");
  const rephrase = useMutation({
    mutationFn: (message: string) =>
      aiApi.rephrase(message, crypto.randomUUID()),
    onMutate: (message) => setSource(message),
  });
  if (!allowed || !status.data?.available) return null;
  const proposal = rephrase.data && source === text ? rephrase.data : null;
  if (proposal && !proposal.unchanged)
    return (
      <aside className="student-draft" aria-label="Version proposée par Permio">
        <p className="student-draft-title">
          <span className="student-draft-icon" aria-hidden="true">
            <Sparkles size={13} />
          </span>
          Version proposée par Permio
          <span className="student-draft-sub">· à relire avant envoi</span>
        </p>
        <p className="student-draft-body">{proposal.text}</p>
        <div className="student-draft-actions">
          <button
            type="button"
            className="btn btn--soft btn--sm"
            disabled={disabled}
            onClick={() => {
              onUse(proposal.text);
              rephrase.reset();
            }}
          >
            Utiliser cette version
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => rephrase.reset()}
          >
            Garder mon texte
          </button>
        </div>
      </aside>
    );
  return (
    <div className="rephrase-row">
      {proposal?.unchanged && (
        <p role="status" className="rephrase-note">
          Votre message est déjà clair : Permio n’y change rien.
        </p>
      )}
      {rephrase.isError && source === text && (
        <p role="alert" className="rephrase-note rephrase-note--error">
          {studentErrorMessage(
            rephrase.error,
            "Permio n’a pas pu reformuler ce message. Réessayez.",
          )}
        </p>
      )}
      <button
        type="button"
        className="btn btn--ghost btn--sm rephrase-button"
        disabled={disabled || !text.trim() || rephrase.isPending}
        onClick={() => rephrase.mutate(text)}
      >
        <Sparkles size={14} aria-hidden="true" />
        {rephrase.isPending ? "Permio reformule…" : "Reformuler"}
      </button>
    </div>
  );
}
